import * as React from 'react';
import { connect } from 'react-redux';
import config from 'config';
import { CUSTOM_MANUFACTURING_ID, CUSTOM_MODEL_ID } from '__common/constants/modules';
import { products, getProductName, isAscender, isNxtHorizon, isSolarMount, isNxtTilt, isCommercialProduct, isRM5, isRMDT, isRmGridflex10D, isRmGridflex5Rwdi, isRM10, isRM10Evolution } from '__common/constants/products';
import { AttachmentType } from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import { TileAttachmentType } from 'projectDesign/components/projectConfiguration/fields/smTileReplacementsOrSolarhooks';
import { RoofSubstrate, RoofType } from 'projectDesign/components/projectConfiguration/utils/constants';

// Lite-create attachment_type default per (product, roof_type). Mirrors
// the helper in liteProjectActions.ts (kept inline here to avoid circular
// dependency with that module). See its block-comment for rationale.
function liteCreateAttachmentDefault(productId: number, roofType: number | null | undefined): number | null | undefined {
  const isNxt = isNxtHorizon(productId);
  const isSm = isSolarMount(productId);
  // RM10 EVO: default to U_ANCHOR_U2400 (new projects are >= 0.0.64).
  // Matches allRms.ts default when uAnchorImprovements is true.
  if (isCommercialProduct(productId)) return AttachmentType.UNIRAC_FLASHLOC_RM;
  if (!isNxt && !isSm) return undefined;
  if (roofType === RoofType.R_PANEL) return AttachmentType.PM_ADJUST;
  if (roofType === RoofType.STANDING_SEAM) return AttachmentType.MINI_1SS;
  if (roofType === RoofType.TILE) return null;
  if (roofType === RoofType.SHINGLE) {
    return isNxt ? AttachmentType.STRONGHOLD_ATT_BUTYL : AttachmentType.SM_BUTYL_DTD;
  }
  return isNxt ? AttachmentType.STRONGHOLD_ATT_KIT_COMP : AttachmentType.L_FOOT_ONLY;
}
import { validateWidth, validateLength } from '__common/components/moduleSelector/components/customModuleValidation';
import { checkThickness } from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/utils/validateProject';
import { nhRoofType, smFlushRoofType } from 'projectDesign/components/projectConfiguration/fields/roofType';
import getRMRoofType from 'projectDesign/components/projectConfiguration/fields/rm5roofType';
import { asce_716 } from '__common/constants/buildingCodes';
import { getDefaultProjectConfiguration } from 'projectDesign/components/projectConfiguration/utils/defaultProps';
import { InputUnit } from 'engineering/components/engineeringProjectDocuments/utils/unitTypes';
import {
  FETCH_MFGS_AND_MODELS_REQUEST,
  FETCH_MFG_MODELS_REQUEST,
  GET_ENVIRONMENTAL_FACTORS,
  CREATE_LITE_PROJECT_REQUEST,
  FETCH_USER_PROFILES_REQUEST,
  CLEAR_PROJECT_CONFIGURATION,
  CLEAR_OBSTRUCTIONS,
} from 'actions';
import {
  SYSTEM_ICONS, ROOF_ICONS, CheckIcon, CloseIcon,
  PersonIcon, LocationPinIcon, GeoResolvedIcon,
} from 'v3/dashboard/icons';
import {
  LITE_BUILDING_HEIGHT,
  LITE_EXPOSURE_CATEGORY,
  LITE_TOPOGRAPHIC_FACTOR,
  getCommercialMandatoryDefaults,
  getLiteRMAdditionalUserInputDefaults,
} from 'v3/dashboard/liteProductDefaults';
import { ROOF_PITCH } from '__editor/panelsEditor/components/tiltedRoof/roofPitch';
import { number } from 'prop-types';

/* ── System types ────────────────────────────────────── */

type SystemType = {
  key: string;
  code: string;
  codes?: string[]; // additional backend codes/subtypes
  label: string;
  type: 'PITCHED' | 'FLAT';
  productId: string;
  productIds?: (keyof typeof products)[];
  enabled: boolean;
};

export const SYSTEM_TYPES: SystemType[] = [
  { key: 'nxt', code: 'NXT', codes: ['NXT', 'NXT_HD'], label: 'Rail System', type: 'PITCHED', productId: 'nxt_umount', productIds: ['nxt_umount', 'nxt_horizon_hd'],enabled: true },
  { key: 'sm', code: 'SM', codes: ['SM', 'SM_LT', 'SM_ASCENDER_FLUSH'], label: 'Rail System', type: 'PITCHED', productId: 'solarmount_2',productIds: [
    'solarmount_2',
    'solarmount_lt',
    'solarmount_ascender_flush',
  ], enabled: true },
  { key: 'rm10', code: 'RM10EVO', codes: ['RM10EVO'], label: 'Bay System', type: 'FLAT', productId: 'rm_10_evolution', productIds: ['rm_10_evolution'], enabled: true },
  { key: 'gf10', code: 'GF10D', codes: ['GF10D'], label: 'Grid System', type: 'FLAT', productId: 'rm_gridflex_10d', productIds: ['rm_gridflex_10d'], enabled: true },
  { key: 'gf5', code: 'GF5D', codes: ['GF5D'], label: 'Grid System', type: 'FLAT', productId: 'rm_gridflex_5d', productIds: ['rm_gridflex_5d'], enabled: true },
];

/* ── Roof types ──────────────────────────────────────── */
// Pulled from existing field definitions so values match the backend.

type RoofOption = { value: number | string; name: string };

const LITE_EXCLUDED_RM_ROOF_VALUES = ['13', '14', '15', '16'];

function getRoofOptionsForSystem(system: SystemType): RoofOption[] {
  const numericId = products[system.productId];
  if (system.key === 'nxt') return nhRoofType.data;
  if (system.key === 'sm') return smFlushRoofType.data;
  if (numericId) {
    return getRMRoofType(numericId, config.currentProjectVersion).data
      .filter((option: RoofOption) => !LITE_EXCLUDED_RM_ROOF_VALUES.includes(String(option.value)));
  }
  return [];
}

/* ── API response types for module selection ─────────── */

type ApiMfg = { id: number; name: string };
type ApiModel = {
  id: number;
  name: string;
  watts?: number;
  max_output_watts?: number;
  width?: string;
  height?: string;
  thickness?: string;
  weight?: string;
};

type ModelSpecs = {
  output: number;
  widthIn: number;
  lengthIn: number;
  thicknessIn: number;
  weightLbs: number;
  areaFt: number;
};

type StoredProfile = {
  id?: number;
  product: number;
  title: string;
  project_config: any;
  is_active?: boolean;
  created_date?: string;
};

/* ── Component ───────────────────────────────────────── */

type Props = {
  onClose: () => void;
  dispatch: Function;
  mfgs: ApiMfg[];
  mfgsLoading: boolean;
  models: ApiModel[];
  modelsLoading: boolean;
  projectEnvConfig: any;
  creating: boolean;
  userProfiles: StoredProfile[];
  userProfilesLoading: boolean;
};

type GeoResult = {
  address: string;
  city: string;
  state: string;
  zipcode: string;
  lat: number;
  lng: number;
};

type State = {
  step: number;
  selectedSystem: SystemType | null;
  projectName: string;
  clientName: string;
  address: string;
  // Geocode (populated from Google Maps Places Autocomplete)
  geoResult: GeoResult | null;
  // Module step
  selectedMfgId: number | null;
  selectedModelId: number | null;
  modelSpecs: ModelSpecs | null;
  unitMetric: boolean;
  // Raw mirror of what the user typed in each spec input. Indexed by the
  // ModelSpecs field key (output, widthIn, lengthIn, thicknessIn, weightLbs).
  // Without this the input is fully controlled off `formatSpec(modelSpecs[...])`,
  // so any keystroke that produces an invalid intermediate (empty string,
  // trailing decimal point, leading zero) gets reformatted away as the user
  // types — they end up locked at the parsed value with no way to edit.
  // Cleared on model change and on Imperial/Metric toggle.
  specRaw: { [field: string]: string };
  // Manufacturer combo
  mfgFilter: string;
  mfgOpen: boolean;
  // Model combo
  modelFilter: string;
  modelOpen: boolean;
  // Roof step
  selectedRoof: string | null;
  // Profile selection
  selectedProfileId: string;
  profileApplied: boolean;
};

class NewProjectModal extends React.Component<Props, State> {
  private mfgRef: React.RefObject<HTMLDivElement>;
  private modelRef: React.RefObject<HTMLDivElement>;
  private addressInputRef: React.RefObject<HTMLInputElement>;
  private autocomplete: any = null;
  private placeChangedListener: any = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      step: 1,
      selectedSystem: null,
      projectName: '',
      clientName: '',
      address: '',
      geoResult: null,
      selectedMfgId: null,
      selectedModelId: null,
      modelSpecs: null,
      unitMetric: false,
      specRaw: {},
      mfgFilter: '',
      mfgOpen: false,
      modelFilter: '',
      modelOpen: false,
      selectedRoof: null,
      selectedProfileId: '',
      profileApplied: false,
    };
    this.mfgRef = React.createRef();
    this.modelRef = React.createRef();
    this.addressInputRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    // Wipe any design state left in Redux by a previously opened project so a
    // new project starts from product defaults. Without this, startDesign's
    // apiOverlay copies the prior project's projectEnvConfig (setback_distance,
    // attachment_config, etc.) into the new config, and the obstructions
    // reducer (keyed by roofId) leaks the prior roof's obstructions onto the
    // new project's roof.
    this.props.dispatch(CLEAR_PROJECT_CONFIGURATION());
    this.props.dispatch(CLEAR_OBSTRUCTIONS());
    this.props.dispatch(FETCH_USER_PROFILES_REQUEST());
  }

  componentDidUpdate(_: Props, prevState: State) {
    // Attach Google Places Autocomplete whenever step 2 mounts the address input.
    if (this.state.step === 2 && prevState.step !== 2) {
      this.attachAutocomplete();
    }
    if (this.state.step !== 2 && prevState.step === 2) {
      this.detachAutocomplete();
    }
    // Fetch environmental factors as soon as step 2 yields a geocode so the
    // design workspace has them ready. Re-fires whenever the user picks a
    // different address.
    const geo = this.state.geoResult;
    const prevGeo = prevState.geoResult;
    const geoChanged = geo && (!prevGeo || prevGeo.lat !== geo.lat || prevGeo.lng !== geo.lng || prevGeo.zipcode !== geo.zipcode);
    if (geoChanged) {
      this.fetchEnvironmentalFactors();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    this.detachAutocomplete();
  }

  attachAutocomplete = () => {
    const input = this.addressInputRef.current;
    const g = (window as any).google;
    if (!input || !g || !g.maps || !g.maps.places) return;
    this.autocomplete = new g.maps.places.Autocomplete(input, { types: ['geocode'] });
    this.placeChangedListener = this.autocomplete.addListener('place_changed', this.handlePlaceChanged);
  };

  detachAutocomplete = () => {
    const g = (window as any).google;
    if (this.placeChangedListener && g && g.maps && g.maps.event) {
      g.maps.event.removeListener(this.placeChangedListener);
    }
    this.placeChangedListener = null;
    this.autocomplete = null;
    // Remove any leftover .pac-container dropdowns Google attaches to <body>.
    document.querySelectorAll('.pac-container').forEach((el) => el.remove());
  };

  handlePlaceChanged = () => {
    if (!this.autocomplete) return;
    const place = this.autocomplete.getPlace();
    if (!place || !place.geometry || !place.geometry.location) return;

    let city = '';
    let state = '';
    let zipcode = '';
    if (place.address_components) {
      place.address_components.forEach((c: any) => {
        if (c.types.includes('postal_code')) zipcode = c.long_name;
        if (c.types.includes('locality')) city = c.short_name;
        if (c.types.includes('administrative_area_level_1')) state = c.short_name;
      });
    }
    if (!city) {
      // Fall back to a sublocality / admin level for places without a `locality`.
      place.address_components?.forEach((c: any) => {
        if (!city && (c.types.includes('sublocality') || c.types.includes('administrative_area_level_2'))) {
          city = c.short_name;
        }
      });
    }

    const loc = place.geometry.location;
    const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
    const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;

    this.setState({
      address: place.formatted_address || '',
      geoResult: {
        address: place.formatted_address || '',
        city,
        state,
        zipcode,
        lat,
        lng,
      },
    });
  };

  handleClickOutside = (e: MouseEvent) => {
    if (this.mfgRef.current && !this.mfgRef.current.contains(e.target as Node)) {
      this.setState({ mfgOpen: false });
    }
    if (this.modelRef.current && !this.modelRef.current.contains(e.target as Node)) {
      this.setState({ modelOpen: false });
    }
  };

  /* ── Step navigation ── */

  goToStep = (n: number) => this.setState({ step: n });

  goToStep2 = () => {
    const { selectedSystem } = this.state;
    if (!selectedSystem) return;
    this.setState({ step: 2 });
  };

  goToStep3 = () => {
    if (this.shouldSkipStepsFromProfile()) {
      this.startDesign();
      return;
    }
    // Close any open dropdowns from previous visits.
    this.setState({ step: 3, mfgOpen: false, modelOpen: false });
    this.fetchManufacturers();
  };

  shouldSkipStepsFromProfile = (): boolean => {
    const { profileApplied, selectedSystem } = this.state;
    return profileApplied && !!selectedSystem;
  };

  fetchManufacturers = () => {
    const { selectedSystem } = this.state;
    if (!selectedSystem) return;
    // Epic fetches mfgs (via api/v1/tool/modules/) then chains an empty
    // models fetch; passing '' for module skips meaningful model results.
    this.props.dispatch(FETCH_MFGS_AND_MODELS_REQUEST(selectedSystem.productId, '', null));
  };

  fetchModels = (mfgId: number) => {
    const { selectedSystem } = this.state;
    if (!selectedSystem) return;
    this.setState({ selectedModelId: null, modelSpecs: null, modelFilter: '' });
    const version = config.currentProjectVersion;
    this.props.dispatch(FETCH_MFG_MODELS_REQUEST(selectedSystem.productId, mfgId, null, undefined, version));
  };

  goToStep4 = () => {
    this.setState({ step: 4, selectedRoof: null });
  };

  goBack = () => {
    this.setState((prev) => ({
      step: Math.max(1, prev.step - 1),
      mfgOpen: false,
      modelOpen: false,
    }));
  };

  /* ── Profiles ── */

  findProfileById = (id: string): StoredProfile | null => {
    const { userProfiles } = this.props;
    if (!id || !Array.isArray(userProfiles)) return null;
    return userProfiles.find((t) => String(t.id) === id) || null;
  };

  resolveSystemFromProfile = (profile: StoredProfile): SystemType | null => {
    const cfg = profile && profile.project_config ? profile.project_config : {};
    const sysMeta = cfg.system || {};
    const code = sysMeta.code || (cfg.project_configuration && cfg.project_configuration.system_code);
    if (code) {
      const match = SYSTEM_TYPES.find(
        (s) => (s.codes ?? [s.code]).includes(code)
      );
      if (match) return match;
    }
    if (profile && typeof profile.product === 'number') {
      const match = SYSTEM_TYPES.find((s) =>
        (s.productIds ?? [s.productId]).some(
          (productId) => products[productId] === profile.product
        )
      );
      if (match) return match;
    }
    return null;
  };

  onProfileSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    if (!id) {
      if (this.state.profileApplied || this.state.selectedProfileId) {
        this.clearProfile();
      } else {
        this.setState({ selectedProfileId: '' });
      }
      return;
    }
    this.applyProfile(id);
  };

  clearProfile = () => {
    this.setState({
      selectedProfileId: '',
      profileApplied: false,
      selectedSystem: null,
      selectedMfgId: null,
      selectedModelId: null,
      modelSpecs: null,
      mfgFilter: '',
      modelFilter: '',
      mfgOpen: false,
      modelOpen: false,
      selectedRoof: null,
      clientName: '',
      address: '',
      geoResult: null,
    });
  };

  applyProfile = (id: string) => {
    const profile = this.findProfileById(id);
    if (!profile) return;
    const system = this.resolveSystemFromProfile(profile);
    if (!system) {
      this.setState({ selectedProfileId: id, profileApplied: false });
      return;
    }
    const cfg = profile.project_config || {};
    const moduleMeta = cfg.module || {};
    const storedPC = cfg.project_configuration || {};
    const specsRaw = moduleMeta.specs || storedPC.module_specs || null;
    const toNumber = (v: any): number => {
      if (typeof v === 'number' && !isNaN(v)) return v;
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    };
    const widthFallback = toNumber(storedPC.module_width);
    const lengthFallback = toNumber(storedPC.module_length);
    const modelSpecs: ModelSpecs | null = specsRaw
      ? {
        output: toNumber(specsRaw.output),
        widthIn: toNumber(specsRaw.widthIn),
        lengthIn: toNumber(specsRaw.lengthIn),
        thicknessIn: toNumber(specsRaw.thicknessIn),
        weightLbs: toNumber(specsRaw.weightLbs),
        areaFt: toNumber(specsRaw.areaFt),
      }
      : (widthFallback || lengthFallback)
        ? {
          output: toNumber(storedPC.module_watts),
          widthIn: widthFallback,
          lengthIn: lengthFallback,
          thicknessIn: toNumber(storedPC.module_thickness),
          weightLbs: toNumber(storedPC.module_weight),
          areaFt: widthFallback && lengthFallback ? (widthFallback * lengthFallback) / 144 : 0,
        }
        : null;
    const mfgIdRaw = moduleMeta.mfg_id ?? storedPC.module_id ?? storedPC.module;
    const modelIdRaw = moduleMeta.model_id ?? storedPC.model_id ?? storedPC.model;
    const mfgId = mfgIdRaw !== null && mfgIdRaw !== undefined && !isNaN(Number(mfgIdRaw)) ? Number(mfgIdRaw) : null;
    const modelId = modelIdRaw !== null && modelIdRaw !== undefined && !isNaN(Number(modelIdRaw)) ? Number(modelIdRaw) : null;
    const mfgName = moduleMeta.mfg_name || storedPC.module_manufacturer || storedPC.manufacturer_name || '';
    const modelName = moduleMeta.model_name || storedPC.module_model || storedPC.model_name || '';
    const roofRaw = cfg.roof_type ?? storedPC.roof_type ?? storedPC.roofType ?? storedPC.roof;
    const roofType = roofRaw !== undefined && roofRaw !== null && roofRaw !== '' ? String(roofRaw) : null;
    const pickStr = (...vals: any[]): string => {
      for (const v of vals) {
        if (typeof v === 'string' && v) return v;
      }
      return '';
    };
    const storedClientName = pickStr(storedPC.client_name, cfg.client_name);
    const storedAddress = pickStr(
      storedPC.client_address,
      storedPC.location,
      cfg.client_address,
      cfg.location,
    );
    const latRaw = storedPC.lat ?? cfg.lat;
    const lngRaw = storedPC.lng ?? cfg.lng;
    const hasLat = latRaw !== null && latRaw !== undefined && latRaw !== '';
    const hasLng = lngRaw !== null && lngRaw !== undefined && lngRaw !== '';
    const storedLat = hasLat ? Number(latRaw) : NaN;
    const storedLng = hasLng ? Number(lngRaw) : NaN;
    const storedZip = pickStr(storedPC.zipcode, cfg.zipcode);
    // Build the geo result from any available location-identifying data.
    // Older profiles may have been saved without client_address/location
    // populated (these come from the FETCH_PROJECT serializer which doesn't
    // always round-trip them), so requiring storedAddress here would drop
    // an otherwise-valid zipcode + lat/lng triple and leave step 2 empty.
    // The downstream gate (fetchEnvironmentalFactors) only checks zipcode.
    const hasGeoCoords = !isNaN(storedLat) && !isNaN(storedLng);
    const hasGeoIdentity = !!storedAddress || !!storedZip || hasGeoCoords;
    const storedGeo: GeoResult | null = hasGeoIdentity
      ? {
        address: storedAddress || storedZip,
        city: '',
        state: '',
        zipcode: storedZip,
        lat: storedLat,
        lng: storedLng,
      }
      : null;
    this.setState({
      selectedProfileId: id,
      profileApplied: true,
      selectedSystem: system,
      selectedMfgId: mfgId,
      selectedModelId: modelId,
      modelSpecs,
      mfgFilter: mfgName,
      modelFilter: modelName,
      mfgOpen: false,
      modelOpen: false,
      selectedRoof: roofType,
      clientName: storedClientName,
      address: storedAddress,
      geoResult: storedGeo,
    });
    if (system && mfgId && modelId) {
      // Pre-populate moduleSelector slice in Redux for the profile skip path:
      // ModuleSection in the design workspace reads dimensions from
      // moduleSelector.modelData — not from projectEnvConfig — so without these
      // calls the workspace shows mfg/model names but blank watts/dims/weight.
      this.props.dispatch(FETCH_MFGS_AND_MODELS_REQUEST(system.productId, '', null));
      this.props.dispatch(FETCH_MFG_MODELS_REQUEST(system.productId, mfgId, modelId, undefined, config.currentProjectVersion));
    }
  };

  /* ── Step 1: System selection ── */

  selectSystem = (system: SystemType) => {
    const prev = this.state.selectedSystem;
    // If user changed the system, clear downstream step-3 state so the next
    // visit to step 3 refetches manufacturers for the new product.
    if (!prev || prev.key !== system.key) {
      this.setState({
        selectedSystem: system,
        mfgFilter: '',
        modelFilter: '',
        selectedMfgId: null,
        selectedModelId: null,
        modelSpecs: null,
        mfgOpen: false,
        modelOpen: false,
        profileApplied: false,
        selectedProfileId: '',
      });
    } else {
      this.setState({ selectedSystem: system });
    }
  };

  /* ── Step 3: Manufacturer / Model ── */

  selectMfg = (mfg: ApiMfg) => {
    this.setState({
      selectedMfgId: mfg.id,
      mfgFilter: mfg.name,
      mfgOpen: false,
      selectedModelId: null,
      modelFilter: '',
      modelSpecs: null,
    });
    this.fetchModels(mfg.id);
  };

  selectModel = (model: ApiModel) => {
    const width = model.width ? parseFloat(model.width) : 0;
    const length = model.height ? parseFloat(model.height) : 0;
    this.setState({
      selectedModelId: model.id,
      modelFilter: model.name,
      modelOpen: false,
      modelSpecs: {
        output: model.watts || model.max_output_watts || 0,
        widthIn: width,
        lengthIn: length,
        thicknessIn: model.thickness ? parseFloat(model.thickness) : 0,
        weightLbs: model.weight ? parseFloat(model.weight) : 0,
        areaFt: width && length ? (width * length) / 144 : 0,
      },
      // Picking a fresh model overrides any in-progress raw input — let
      // the formatted modelSpecs values render until the user edits again.
      specRaw: {},
    });
  };

  getFilteredMfgs = (): ApiMfg[] => {
    const { mfgs } = this.props;
    const q = this.state.mfgFilter.toLowerCase();
    if (!q) return mfgs;
    if (mfgs.some((m) => m.name.toLowerCase() === q)) return mfgs;
    return mfgs.filter((m) => m.name.toLowerCase().includes(q));
  };

  getFilteredModels = (): ApiModel[] => {
    const { models } = this.props;
    const q = this.state.modelFilter.toLowerCase();
    if (!q) return models;
    return models.filter((m) => m.name.toLowerCase().includes(q));
  };

  formatSpec = (imperial: number, toMetric: number, decimals: number): string => {
    if (this.state.unitMetric) {
      return (imperial * toMetric).toFixed(decimals);
    }
    return imperial.toFixed(decimals);
  };

  updateSpec = (field: keyof ModelSpecs, raw: string, toImperialMultiplier: number) => {
    const { modelSpecs, selectedMfgId, selectedModelId, specRaw } = this.state;
    if (!modelSpecs) return;
    // Always keep the literal raw string the user just typed — that's what
    // the input renders. Parsing/reformatting only feeds modelSpecs (used
    // for downstream area calc + validators).
    const nextSpecRaw = { ...specRaw, [field]: raw };
    // Empty or non-numeric → leave modelSpecs[field] at 0 so validators flag
    // it, but don't try to multiply NaN. The user can keep typing freely;
    // `value` reads from specRaw, not modelSpecs.
    const parsed = parseFloat(raw);
    const imperialValue = (raw === '' || isNaN(parsed)) ? 0 : parsed * toImperialMultiplier;
    const next: ModelSpecs = { ...modelSpecs, [field]: imperialValue };
    if (field === 'widthIn' || field === 'lengthIn') {
      next.areaFt = (next.widthIn * next.lengthIn) / 144;
    }
    const alreadyCustom = selectedMfgId === CUSTOM_MANUFACTURING_ID && selectedModelId === CUSTOM_MODEL_ID;
    if (alreadyCustom) {
      this.setState({ modelSpecs: next, specRaw: nextSpecRaw });
    } else {
      this.setState({
        modelSpecs: next,
        specRaw: nextSpecRaw,
        selectedMfgId: CUSTOM_MANUFACTURING_ID,
        selectedModelId: CUSTOM_MODEL_ID,
        mfgFilter: 'Custom',
        modelFilter: 'Custom',
        mfgOpen: false,
        modelOpen: false,
      });
    }
  };

  /* ── Step 4: Roof type ── */

  getRoofOptions = (): RoofOption[] => {
    const { selectedSystem } = this.state;
    if (!selectedSystem) return [];
    return getRoofOptionsForSystem(selectedSystem);
  };

  /* ── Validation ── */

  canContinue = (): boolean => {
    const { step, selectedSystem, projectName, address, selectedModelId, selectedRoof, modelSpecs } = this.state;
    switch (step) {
      case 1: return !!selectedSystem;
      case 2: return projectName.trim().length > 0 && address.trim().length > 0;
      case 3: {
        if (!selectedModelId || !modelSpecs) return false;
        const errs = this.getModuleSpecErrors();
        return !errs.width && !errs.length && !errs.thickness;
      }
      case 4: return !!selectedRoof;
      default: return false;
    }
  };

  // Mirrors legacy customModuleSelector validation (validateWidth /
  // validateLength / checkThickness + product-specific thickness messages
  // from customModuleSelector.tsx:503). Lite Step 3 lets the user override
  // the dimensions of any module — without this, projects could be created
  // with thicknesses up to 2"+ or out-of-range widths/lengths that legacy
  // would have rejected. Runs against modelSpecs (always imperial inches /
  // lbs); the metric/imperial toggle in the UI is display-only.
  getModuleSpecErrors = (): { width?: string; length?: string; thickness?: string } => {
    const { selectedSystem, modelSpecs } = this.state;
    if (!selectedSystem || !modelSpecs) return {};
    const productId = products[selectedSystem.productId];
    if (!productId) return {};
    const errs: { width?: string; length?: string; thickness?: string } = {};

    const widthErr = validateWidth(modelSpecs.widthIn, productId, false, InputUnit.CUSTOMARY);
    if (widthErr) errs.width = widthErr;

    const lengthErr = validateLength(modelSpecs.lengthIn, productId, InputUnit.CUSTOMARY);
    if (lengthErr) errs.length = lengthErr;

    if (!checkThickness(modelSpecs.thicknessIn, productId)) {
      errs.thickness = this.thicknessErrorMessage(productId);
    }
    return errs;
  };

  // Product-specific thickness range message, mirroring
  // customModuleSelector.tsx's errMsg() switch.
  private thicknessErrorMessage = (productId: number): string => {
    if (isRM5(productId) || isRMDT(productId) || isRM10Evolution(productId)) {
      return 'Module thickness should be >= 30mm / 1.18in and <= 50mm / 1.96in.';
    }
    if (isRmGridflex10D(productId)) {
      return 'Module thickness should be >= 30mm / 1.18in and <= 45mm / 1.77in.';
    }
    if (isRmGridflex5Rwdi(productId)) {
      return 'Module thickness should be >= 30mm / 1.18in and <= 45mm / 1.77in.';
    }
    if (isRM10(productId)) {
      return 'Thickness should be greater than 0.';
    }
    if (isNxtHorizon(productId) || isNxtTilt(productId)) {
      return 'Module thickness should be >= 30mm / 1.18in and <= 40mm / 1.57in.';
    }
    return 'Minimum Module thickness is 30mm / 1.18in.';
  };

  /* ── Defaults / env factors ── */

  // Lite forces ASCE 7-16 for all 5 supported products (per Lite spec). Legacy
  // UB resolves the code per-family via isOnlyAsce716ProductFamily, but that
  // leaves plain SM Flush / NXT Horizon / RM products on 7-10 — and the panel
  // zone classifier explicitly sets roofZone = 0 on blank-map + non-7-16/22
  // (panels.ts:328), which then breaks the SM Flush BOM calc (zone=0 has no
  // calc path, hence PR #5154's `if zone==0: zone=1` band-aid). Forcing 7-16
  // here makes the zone classifier hit DEFAULT_CLASSIC_DESIGNER_ZONE and emit
  // the correct zone (1 for residential, 2 for commercial). Ascender → metric.
  resolveProductDefaults = (_numericProductId: number) => ({
    buildingCode: asce_716.value,
    riskCategory: 2,
    inputUnit: isAscender(_numericProductId) ? InputUnit.METRIC : InputUnit.CUSTOMARY,
  });

  // On address entry, resolve coordinates from the ZIP via geocode_from_zipcode
  // first, then fetch environmental factors with those coordinates. This is the
  // single place these two APIs fire — the design page no longer fetches on load.
  fetchEnvironmentalFactors = () => {
    const { selectedSystem, geoResult } = this.state;
    if (!selectedSystem || !geoResult || !geoResult.zipcode) return;
    const numericProductId = products[selectedSystem.productId];
    if (!numericProductId) return; // Skip if the slug isn't mapped to a numeric id.
    const { buildingCode, riskCategory } = this.resolveProductDefaults(numericProductId);
    const { zipcode } = geoResult;
    const apiBase = (config as any).apiUrl;
    const url = `${window.location.protocol}//${apiBase}api/v1/geocode_from_zipcode/${zipcode}/`;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((body: any) => {
        // Prefer backend-geocoded coordinates; fall back to Google Places coords.
        const lat = body && typeof body.lat === 'number' ? body.lat : geoResult.lat;
        const lng = body && typeof body.lng === 'number' ? body.lng : geoResult.lng;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        this.props.dispatch(
          GET_ENVIRONMENTAL_FACTORS(lat, lng, zipcode, buildingCode, numericProductId, riskCategory),
        );
      })
      .catch(() => {/* swallow — env factors just won't prefill */});
  };

  /* ── Start design ── */

  startDesign = () => {
    const { selectedSystem, projectName, clientName, geoResult, selectedMfgId, selectedModelId, selectedRoof, modelSpecs, mfgFilter, modelFilter, profileApplied, selectedProfileId } = this.state;
    if (!selectedSystem) return;
    let numericProductId = products[selectedSystem.productId];
    if (!numericProductId) return;
    // When a profile is applied, use the actual rail product stored in the
    // profile (e.g. NXT HD / SM Light) rather than the system family default.
    // `selectedSystem.productId` is always the family default (nxt_umount /
    // solarmount_2), so without this a profile saved from a NXT HD or SM Light
    // project creates the new project with the default rail and the URL uses
    // the default slug. The profile's `product` field holds the specific
    // numeric product id that was active when the profile was saved.
    let productNameSlug = selectedSystem.productId;
    let systemCode = selectedSystem.code;
    if (profileApplied && selectedProfileId) {
      const profile = this.findProfileById(selectedProfileId);
      const profileProductId = profile && typeof profile.product === 'number' ? profile.product : null;
      if (profileProductId) {
        const slug = getProductName(profileProductId);
        if (slug && (selectedSystem.productIds ?? [selectedSystem.productId]).includes(slug)) {
          numericProductId = profileProductId;
          productNameSlug = slug;
          const idx = (selectedSystem.productIds ?? [selectedSystem.productId]).indexOf(slug);
          if (idx >= 0 && selectedSystem.codes && selectedSystem.codes[idx]) {
            systemCode = selectedSystem.codes[idx];
          }
        }
      }
    }
    const selectedMfg = this.props.mfgs.find((m) => m.id === selectedMfgId);
    const selectedModel = this.props.models.find((m) => m.id === selectedModelId);
    const mfgDisplayName = selectedMfg ? selectedMfg.name : mfgFilter;
    const modelDisplayName = selectedModel ? selectedModel.name : modelFilter;
    const { buildingCode, riskCategory, inputUnit } = this.resolveProductDefaults(numericProductId);
    const defaults = getDefaultProjectConfiguration(numericProductId) || {};
    // Preserve env factors fetched during step 2, but drop them if the zipcode
    // came back BLANK — otherwise nulls would overwrite the product defaults.
    const apiEnv = this.props.projectEnvConfig || {};
    const blankOccurred = !!apiEnv.env_factors_for
      && (apiEnv.wind_speed === null || apiEnv.wind_speed === undefined);
    const ENV_FACTOR_KEYS = [
      'wind_speed', 'wind_speed_i', 'wind_speed_ii', 'wind_speed_iii',
      'snow_load', 'seismic_ss', 'seismic_s1', 'seismic',
      'topographical_factor_kzt', 'env_factors_for',
    ];
    const apiOverlay: any = {};
    if (!blankOccurred) {
      Object.keys(apiEnv).forEach((k) => {
        const v = apiEnv[k];
        if (v !== null && v !== undefined) apiOverlay[k] = v;
      });
    }
    const profileOverlay: any = {};
    let storedProfilePC: any = null;
    let storedProfileAdvanced: any = null;
    if (profileApplied && selectedProfileId) {
      const profile = this.findProfileById(selectedProfileId);
      const stored = profile && profile.project_config && profile.project_config.project_configuration;
      const storedAdvanced = profile && profile.project_config && profile.project_config.advanced_settings;
      if (stored && typeof stored === 'object') {
        storedProfilePC = stored;
        Object.keys(stored).forEach((k) => {
          const v = stored[k];
          if (v !== null && v !== undefined && ENV_FACTOR_KEYS.indexOf(k) === -1) {
            profileOverlay[k] = v;
          }
        });
      }
      if (storedAdvanced && typeof storedAdvanced === 'object') {
        storedProfileAdvanced = storedAdvanced;
      }
    }
    const advancedFromProfile = (key: string): any => {
      if (storedProfileAdvanced && storedProfileAdvanced[key] !== null && storedProfileAdvanced[key] !== undefined) {
        return storedProfileAdvanced[key];
      }
      if (storedProfilePC && storedProfilePC[key] !== null && storedProfilePC[key] !== undefined) {
        return storedProfilePC[key];
      }
      return undefined;
    };
    const prefix = `${selectedSystem.code} - `;
    const finalName = projectName && !projectName.startsWith(prefix)
      ? `${prefix}${projectName}`
      : projectName;
    // When a profile is applied, fall back to the profile's stored values for
    // module IDs / specs / roof_type so an incomplete local state (e.g. specs
    // not yet hydrated from FETCH_MFG_MODELS) doesn't overwrite valid profile
    // data with null/undefined.
    const profileMfgId = storedProfilePC ? (storedProfilePC.module_id ?? storedProfilePC.module) : null;
    const profileModelId = storedProfilePC ? (storedProfilePC.model_id ?? storedProfilePC.model) : null;
    const profileRoof = storedProfilePC ? (storedProfilePC.roof_type ?? storedProfilePC.roofType ?? storedProfilePC.roof) : null;
    const finalMfgId = (selectedMfgId !== null && selectedMfgId !== undefined) ? selectedMfgId
      : (profileMfgId !== null && profileMfgId !== undefined ? profileMfgId : null);
    const finalModelId = (selectedModelId !== null && selectedModelId !== undefined) ? selectedModelId
      : (profileModelId !== null && profileModelId !== undefined ? profileModelId : null);
    const finalRoof = (selectedRoof !== null && selectedRoof !== undefined && selectedRoof !== '') ? selectedRoof
      : (profileRoof !== null && profileRoof !== undefined && profileRoof !== '' ? String(profileRoof) : null);
    const finalMfgName = mfgDisplayName || (storedProfilePC ? (storedProfilePC.manufacturer_name || storedProfilePC.module_manufacturer) : '') || '';
    const finalModelName = modelDisplayName || (storedProfilePC ? (storedProfilePC.model_name || storedProfilePC.module_model) : '') || '';
    const finalWatts = modelSpecs ? modelSpecs.output : (storedProfilePC ? storedProfilePC.module_watts : undefined);
    const finalWidth = modelSpecs ? modelSpecs.widthIn : (storedProfilePC ? storedProfilePC.module_width : undefined);
    const finalLength = modelSpecs ? modelSpecs.lengthIn : (storedProfilePC ? storedProfilePC.module_length : undefined);
    const finalThickness = modelSpecs ? modelSpecs.thicknessIn : (storedProfilePC ? storedProfilePC.module_thickness : undefined);
    const finalWeight = modelSpecs ? modelSpecs.weightLbs : (storedProfilePC ? storedProfilePC.module_weight : undefined);
    const finalSpecs = modelSpecs || (storedProfilePC ? storedProfilePC.module_specs : null);
    // Advanced section fields. Resolve each to: profile value (if profile is
    // applied) → defaults (matching buildLiteUpdatePayload). These are placed
    // in the explicit override block below so a falsy/missing value in
    // `defaults` cannot shadow a valid profile value via spread order.
    const advBuildingHeight = advancedFromProfile('building_height') ?? profileOverlay.building_height ?? LITE_BUILDING_HEIGHT[numericProductId];
    const advRoofPitchRaw = advancedFromProfile('roof_pitch') ?? profileOverlay.roof_pitch;
    // roof_pitch is an IntegerField(choices=ROOF_PITCH_CHOICE) on the backend.
    // Profile can carry a number (already remapped at save time), a "3/12"-style
    // label from a legacy save, or a degree string like "14" from Lite's
    // AdvancedSection dropdown. Coerce labels via the ROOF_PITCH map; degree
    // strings are not keys in that map, so fall back to Number() to avoid
    // zeroing out a valid pitch — mirrors buildLiteUpdatePayload.
    let advRoofPitch: number | undefined;
    if (typeof advRoofPitchRaw === 'number') {
      advRoofPitch = advRoofPitchRaw;
    } else if (typeof advRoofPitchRaw === 'string') {
      advRoofPitch = (ROOF_PITCH as any)[advRoofPitchRaw]
        ?? (isNaN(Number(advRoofPitchRaw)) ? 0 : Number(advRoofPitchRaw));
    } else {
      advRoofPitch = undefined;
    }
    // Profiles saved from Lite before the degree-string fix stored
    // roof_pitch=0 because the per-roof value (e.g. "14") wasn't found in
    // the ROOF_PITCH map. A 0 pitch is never valid for pitched products
    // (SM/NXT) and breaks the BOM; treat it as undefined so the backend
    // applies its own default, matching the no-profile create path.
    if (advRoofPitch === 0 && (isNxtHorizon(numericProductId) || isSolarMount(numericProductId))) {
      advRoofPitch = undefined;
    }
    const advShortestBuildingLength = advancedFromProfile('shortest_building_length') ?? profileOverlay.shortest_building_length;
    const advBlockWeight = advancedFromProfile('block_weight') ?? profileOverlay.block_weight;
    const advAttachmentConfig = advancedFromProfile('attachment_config') ?? profileOverlay.attachment_config ?? 'Standard';
    const advStaggerRaw = advancedFromProfile('stagger_attachments');
    const advStaggerAttachments = typeof advStaggerRaw === 'boolean' ? advStaggerRaw
      : (typeof profileOverlay.stagger_attachments === 'boolean' ? profileOverlay.stagger_attachments : false);
    const advRailDirection = advancedFromProfile('rail_direction') ?? profileOverlay.rail_direction ?? 'EW';
    const advRafterSpacing = advancedFromProfile('rafter_spacing_inches') ?? profileOverlay.rafter_spacing_inches ?? 24;
    const advPreferredSpan = advancedFromProfile('preferred_span') ?? profileOverlay.preferred_span;
    const advAttachmentType = advancedFromProfile('attachment_type') ?? profileOverlay.attachment_type;
    const advRoofSubstrate = advancedFromProfile('roof_substrate') ?? profileOverlay.roof_substrate;
    const project_configuration: any = {
      ...defaults,
      ...apiOverlay,
      ...profileOverlay,
      product_id: numericProductId,
      productId: numericProductId,
      custom_title: finalName,
      zipcode: geoResult ? geoResult.zipcode : defaults.zipcode,
      client_address: geoResult ? geoResult.address : '',
      location: geoResult ? geoResult.address : '',
      lat: geoResult && Number.isFinite(geoResult.lat) ? geoResult.lat : undefined,
      lng: geoResult && Number.isFinite(geoResult.lng) ? geoResult.lng : undefined,
      client_name: clientName,
      module_id: finalMfgId,
      model_id: finalModelId,
      manufacturer_name: finalMfgName,
      model_name: finalModelName,
      module_watts: finalWatts,
      module_width: finalWidth,
      module_length: finalLength,
      module_thickness: finalThickness,
      module_weight: finalWeight,
      module_specs: finalSpecs,
      building_code: profileOverlay.building_code ?? buildingCode,
      risk_category: profileOverlay.risk_category ?? riskCategory,
      input_unit: profileOverlay.input_unit ?? inputUnit,
      roof_type: finalRoof,
      system_key: selectedSystem.key,
      system_code: systemCode,
      system_label: selectedSystem.label,
      system_type: selectedSystem.type,
      // Lite default from docs/U-Builder Lite UX Prototype Left Technical Info.xlsx:
      // wind_exposure = 'C' (OPEN_TERRAIN = exposure_category 2 per
      // backend/tool/constants.py WindExposure + view's exposure_map remap).
      exposure_category: LITE_EXPOSURE_CATEGORY,
      wind_exposure: profileOverlay.wind_exposure ?? apiOverlay.wind_exposure ?? 'C',
      topographic_factor: profileOverlay.topographic_factor ?? LITE_TOPOGRAPHIC_FACTOR,
      // Advanced-section fields. Profile-supplied values authoritatively win
      // over defaults via the resolved adv* variables.
      building_height: advBuildingHeight,
      rail_direction: advRailDirection,
      rafter_spacing_inches: advRafterSpacing,
      attachment_config: advAttachmentConfig,
      stagger_attachments: advStaggerAttachments,
      // Lite default for attachment_type per (product, roof_type), mirroring
      // legacy attachmentType.ts's smAttachmentData / nhAttachmentData first
      // option per roof, except SHINGLE which we override to Butyl per spec:
      //   SHINGLE → Butyl (NXT: STRONGHOLD_ATT_BUTYL, SM: SM_BUTYL_DTD)
      //   R_PANEL → PM_ADJUST   (only valid R_PANEL options are PM_ADJUST/PM_9000S)
      //   other   → first of legacy list (NXT: STRONGHOLD_ATT_KIT_COMP, SM: L_FOOT_ONLY)
      // Without this, R_PANEL projects ship Butyl and crash the SM/NXT
      // span calc which doesn't recognise Butyl on metal roofs.
      attachment_type: advAttachmentType ?? liteCreateAttachmentDefault(numericProductId, Number(finalRoof)),
      row_spacing: getRowSpacingLite(numericProductId),
      column_spacing: getColSpacingLite(numericProductId),
      tile_replacement_or_solarhooks: Number(finalRoof) === RoofType.TILE ? TileAttachmentType.TILE_REPLACEMENT : null,
      // roof_substrate only required by the Butyl-on-Shingle BOM path
      // (nxt_horizon_span_calcs.py:get_max_down has no else for null
      // substrate). Default RAFTERS for the Butyl case so the BOM doesn't
      // crash; safe to send for non-Butyl too — backend ignores it.
      roof_substrate: advRoofSubstrate ?? ((isNxtHorizon(numericProductId) || isSolarMount(numericProductId))
        ? RoofSubstrate.RAFTERS
        : undefined),
    };
    if (advRoofPitch !== undefined) {
      project_configuration.roof_pitch = advRoofPitch;
    } else {
      // Drop any roof_pitch that survived from profileOverlay's spread (e.g.
      // a "3/12"-style string from drawingManager) — backend expects an int.
      delete project_configuration.roof_pitch;
    }
    if (advShortestBuildingLength !== undefined) project_configuration.shortest_building_length = advShortestBuildingLength;
    if (advBlockWeight !== undefined) project_configuration.block_weight = advBlockWeight;
    if (advPreferredSpan !== undefined) project_configuration.preferred_span = advPreferredSpan;
    // When a stored profile was applied as the starting point of this new
    // project, persist a `profile_id` ProjectVariable so the design workspace
    // can later resolve the current profile from the project state on reload
    // and on the "Save as Profile" modal. Backend's UbLiteProjectSetupTask
    // loop (api/ub_lite_tasks.py) pops every key matching a Variable.name and
    // writes the value to a ProjectVariable row. Requires a Variable row with
    // name='profile_id' to exist server-side.
    if (profileApplied && selectedProfileId) {
      project_configuration.profile_id = String(selectedProfileId);
    }
    // attachment_config: PR #5132 added this field to UbLiteProjectSerializer
    // and the str->int remap to UPDATE only — CREATE now validates the raw
    // value against the model's PositiveSmallIntegerField choices, so a label
    // leaked in via projectEnvConfig (set by AdvancedSection's pitched
    // defaults in a prior session) makes POST /ub_lite/project fail with
    // "Min. Attachment is not a valid choice." Mirror the backend's
    // ATTACHMENT_CONFIG_CHOICES_VALUES on the create path so the label is
    // converted before send. Keep buildLiteUpdatePayload sending the label
    // because UPDATE still relies on the backend remap.
    const ATTACHMENT_CONFIG_LABEL_TO_INT: { [k: string]: number } = {
      'Min. Attachment': 1,
      'Standard': 2,
      'Staggered': 3,
      'Inline': 4,
    };
    const attachmentConfigLabel = typeof project_configuration.attachment_config === 'string'
      ? project_configuration.attachment_config
      : null;
    const ac = project_configuration.attachment_config;
    if (typeof ac === 'string' && ATTACHMENT_CONFIG_LABEL_TO_INT[ac] !== undefined) {
      project_configuration.attachment_config = ATTACHMENT_CONFIG_LABEL_TO_INT[ac];
    }
    if (profileApplied) {
      try {
        const pending: any = {
          ts: Date.now(),
          productId: numericProductId,
          fields: {
            building_height: advBuildingHeight,
            rail_direction: advRailDirection,
            rafter_spacing_inches: advRafterSpacing,
            attachment_config: attachmentConfigLabel,
            stagger_attachments: advStaggerAttachments,
          },
        };
        if (advRoofPitch !== undefined) pending.fields.roof_pitch = advRoofPitch;
        if (advShortestBuildingLength !== undefined) pending.fields.shortest_building_length = advShortestBuildingLength;
        if (advBlockWeight !== undefined) pending.fields.block_weight = advBlockWeight;
        if (advPreferredSpan !== undefined) pending.fields.preferred_span = advPreferredSpan;
        window.localStorage.setItem('lite_pending_profile_advanced', JSON.stringify(pending));
      } catch (_) { /* ignore storage errors */ }
    }
    // RM10 EVO (and other commercial products) require mandatory fields
    // (manufacturer, colour, parapet_height, building lengths, roof_pitch,
    // sub_category) before the panel editor can function. In original UBuilder
    // these are filled via the "Building and Racking" flyout; in Lite we inject
    // sensible defaults (first valid value per field) so the editor works
    // immediately. See RM10_Mandatory_Fields_by_Roof_Type.xlsx for the matrix.
    if (isCommercialProduct(numericProductId)) {
      const commercialDefaults = getCommercialMandatoryDefaults(numericProductId, finalRoof);
      Object.keys(commercialDefaults).forEach(key => {
        if (project_configuration[key] === undefined || project_configuration[key] === null) {
          project_configuration[key] = commercialDefaults[key];
        }
      });
      // Lite defaults to NO — override allRms.ts default of 1
      project_configuration.allow_mechanical_attachments = 1;
      // Additional User Inputs defaults for GF/RM products — legacy fills
      // these via the Additional User Inputs flyout; Lite seeds them here.
      const additionalDefaults = getLiteRMAdditionalUserInputDefaults(
        numericProductId,
        project_configuration,
        finalLength,
      );
      Object.keys(additionalDefaults).forEach(key => {
        if (
          key === 'use_default_flat_slope_roof_snow_load' ||
          key === 'flat_roof_snow_load' ||
          key === 'thermal_factor' ||
          project_configuration[key] === undefined ||
          project_configuration[key] === null
        ) {
          project_configuration[key] = additionalDefaults[key];
        }
      });
    }

    // Epic POSTs this to /api/v1/ub_lite/project/, then chains the existing
    // FETCH_PROJECT + navigates to /project/lite/design/:productId/:projectId.
    this.props.dispatch(CREATE_LITE_PROJECT_REQUEST({
      data: { project_configuration, roofs: [] },
      productId: numericProductId,
      productName: productNameSlug,
    }));
  };

  onContinue = () => {
    const { step } = this.state;
    if (step === 1) this.goToStep2();
    else if (step === 2) this.goToStep3();
    else if (step === 3) this.goToStep4();
    else if (step === 4) this.startDesign();
  };

  /* ── Render helpers ── */

  renderProgressBar() {
    const { step } = this.state;
    return (
      <div className="v3-modal-progress">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`v3-modal-progress-seg${s < step ? ' done' : ''}${s === step ? ' active' : ''}`}
          />
        ))}
      </div>
    );
  }

  renderStep1() {
    const { selectedSystem, selectedProfileId } = this.state;
    if (selectedSystem && isSolarMount(Number(selectedSystem.key))){
      selectedSystem.key='9';
    }
    if (selectedSystem && isNxtHorizon(Number(selectedSystem.key))){
      selectedSystem.key='25';
    }
    const { userProfiles } = this.props;
    const hasProfiles = Array.isArray(userProfiles) && userProfiles.length > 0;
    return (
      <div className="v3-modal-body">
        {hasProfiles && (
          <div className="v3-prf-banner">
            <div className="v3-modal-field" style={{ marginBottom: 0 }}>
              <label>
                Start from a profile <span className="v3-prf-banner-optional">(optional)</span>
              </label>
              <select
                className="v3-modal-input v3-modal-select v3-prf-select"
                value={selectedProfileId}
                onChange={this.onProfileSelectChange}
              >
                <option value="">— Select a profile —</option>
                {userProfiles.map((t) => {
                  const sysCode = (t.project_config && t.project_config.system && t.project_config.system.code) || '';
                  const label = sysCode ? `[${sysCode}] ${t.title}` : t.title;
                  return (
                    <option key={String(t.id)} value={String(t.id)}>{label}</option>
                  );
                })}
              </select>
            </div>
            <div className="v3-prf-divider" />
          </div>
        )}
        <div className="v3-mini-card-wrap">
          {SYSTEM_TYPES.map((sys) => (
            <div
              key={sys.key}
              className={`v3-mini-card${selectedSystem?.key === sys.key ? ' selected' : ''}${!sys.enabled ? ' disabled' : ''}`}
              onClick={() => sys.enabled && this.selectSystem(sys)}
            >
              {selectedSystem?.key === sys.key && (
                <div className="v3-mini-card-check">
                  <CheckIcon />
                </div>
              )}
              <div className="v3-mini-card-icon">{SYSTEM_ICONS[sys.code]}</div>
              <div className="v3-mini-card-code">{sys.code}</div>
              <div className="v3-mini-card-sub">{sys.label}</div>
              <div className={`v3-mini-card-tag ${sys.type === 'PITCHED' ? 'pitched' : 'flat'}`}>
                {sys.type}
              </div>
              {!sys.enabled && <div className="v3-mini-card-coming-soon">Coming Soon</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderStep2() {
    const { selectedSystem, projectName, clientName, address, geoResult } = this.state;
    return (
      <div className="v3-modal-body">
        <div className="v3-modal-field">
          <label>Project Name</label>
          <div className="v3-modal-prefix-wrap">
            <div className="v3-modal-prefix-tag">{selectedSystem?.code} -</div>
            <input
              className="v3-modal-input"
              type="text"
              placeholder="e.g. Rosewood Residential Phase 1"
              value={projectName}
              onChange={(e) => this.setState({ projectName: e.target.value })}
              autoFocus
            />
          </div>
        </div>

        <div className="v3-modal-field">
          <label>Client Name</label>
          <div className="v3-modal-input-icon">
            <span className="v3-mi-icon">
              <PersonIcon />
            </span>
            <input
              className="v3-modal-input has-icon"
              type="text"
              placeholder="e.g. Jordan Hartley"
              value={clientName}
              onChange={(e) => this.setState({ clientName: e.target.value })}
            />
          </div>
        </div>

        <div className="v3-modal-field">
          <label>Installation Address</label>
          <div className="v3-modal-input-icon">
            <span className="v3-mi-icon">
              <LocationPinIcon />
            </span>
            <input
              ref={this.addressInputRef}
              className="v3-modal-input has-icon"
              type="text"
              placeholder="Start typing an address or ZIP…"
              value={address}
              onChange={(e) => this.setState({ address: e.target.value, geoResult: null })}
              autoComplete="off"
            />
          </div>
          {geoResult ? (
            <span className="v3-modal-field-hint v3-geo-resolved">
              <GeoResolvedIcon />
              {[geoResult.city, geoResult.state, geoResult.zipcode].filter(Boolean).join(', ')}
            </span>
          ) : (
            <span className="v3-modal-field-hint">Building code and load data will be fetched from your address.</span>
          )}
        </div>
      </div>
    );
  }

  renderStep3() {
    const { selectedMfgId, modelSpecs, unitMetric, specRaw, mfgFilter, mfgOpen, modelFilter, modelOpen } = this.state;
    const { mfgsLoading, modelsLoading } = this.props;
    const filteredMfgs = this.getFilteredMfgs();
    const filteredModels = this.getFilteredModels();
    const hasSpecs = !!modelSpecs;
    const specErrors = hasSpecs ? this.getModuleSpecErrors() : {};
    // Helper: prefer the raw string the user just typed over the formatted
    // numeric. Without this, intermediate states ("0.", "1.5", empty) get
    // reformatted on every keystroke and the user can't edit at all.
    const specValue = (field: keyof ModelSpecs, formatted: string): string =>
      specRaw[field] !== undefined ? specRaw[field] : formatted;

    return (
      <div className="v3-modal-body">
        <div className="v3-modal-row2" style={{ marginBottom: 16 }}>
          {/* Manufacturer combo */}
          <div className="v3-modal-field" style={{ marginBottom: 0 }} ref={this.mfgRef}>
            <label>Manufacturer</label>
            <div className="v3-mfr-combo">
              <input
                className="v3-modal-input"
                type="text"
                placeholder={mfgsLoading ? 'Loading manufacturers…' : 'Type or select…'}
                value={mfgFilter}
                onChange={(e) => this.setState({ mfgFilter: e.target.value, mfgOpen: true, selectedMfgId: null, selectedModelId: null, modelFilter: '', modelSpecs: null })}
                onFocus={() => this.setState({ mfgOpen: true })}
                autoComplete="off"
              />
              {mfgOpen && filteredMfgs.length > 0 && (
                <ul className="v3-mfr-dropdown open">
                  {filteredMfgs.map((mfg) => (
                    <li key={mfg.id} onClick={() => this.selectMfg(mfg)}>{mfg.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Model combo */}
          <div className="v3-modal-field" style={{ marginBottom: 0 }} ref={this.modelRef}>
            <label>Model</label>
            <div className="v3-mfr-combo">
              <input
                className="v3-modal-input"
                type="text"
                placeholder={modelsLoading ? 'Loading models…' : selectedMfgId ? 'Type or select…' : 'Select a manufacturer first'}
                value={modelFilter}
                onChange={(e) => selectedMfgId && this.setState({ modelFilter: e.target.value, modelOpen: true, selectedModelId: null, modelSpecs: null })}
                onFocus={() => selectedMfgId && this.setState({ modelOpen: true })}
                autoComplete="off"
                disabled={!selectedMfgId}
              />
              {selectedMfgId && modelOpen && filteredModels.length > 0 && (
                <ul className="v3-mfr-dropdown open">
                  {filteredModels.map((model) => (
                    <li key={model.id} onClick={() => this.selectModel(model)}>{model.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Specs table */}
        <div style={{ opacity: hasSpecs ? 1 : 0.35, pointerEvents: hasSpecs ? 'auto' : 'none', transition: 'opacity .2s' }}>
          <div className="v3-specs-unit-bar">
            <span className="v3-specs-unit-bar-lbl">Units</span>
            <div className="v3-spec-unit-toggle">
              <button className={!unitMetric ? 'active' : ''} onClick={() => this.setState({ unitMetric: false, specRaw: {} })}>Imperial</button>
              <button className={unitMetric ? 'active' : ''} onClick={() => this.setState({ unitMetric: true, specRaw: {} })}>Metric</button>
            </div>
          </div>
          <div className="v3-specs-table">
            <div className="v3-specs-row">
              <span className="v3-spec-lbl">Output</span>
              <input
                className="v3-spec-val"
                type="number"
                value={modelSpecs ? specValue('output', String(modelSpecs.output)) : ''}
                placeholder="—"
                onChange={(e) => this.updateSpec('output', e.target.value, 1)}
              />
              <span className="v3-spec-unit">W</span>
            </div>
            <div className="v3-specs-row">
              <span className="v3-spec-lbl">Width</span>
              <input
                className={`v3-spec-val${specErrors.width ? ' v3-spec-val-error' : ''}`}
                type="number"
                value={modelSpecs ? specValue('widthIn', this.formatSpec(modelSpecs.widthIn, 25.4, unitMetric ? 0 : 1)) : ''}
                placeholder="—"
                onChange={(e) => this.updateSpec('widthIn', e.target.value, unitMetric ? 1 / 25.4 : 1)}
              />
              <span className="v3-spec-unit">{unitMetric ? 'mm' : 'in'}</span>
            </div>
            {specErrors.width && (
              <div className="v3-specs-row-err">{specErrors.width}</div>
            )}
            <div className="v3-specs-row">
              <span className="v3-spec-lbl">Length</span>
              <input
                className={`v3-spec-val${specErrors.length ? ' v3-spec-val-error' : ''}`}
                type="number"
                value={modelSpecs ? specValue('lengthIn', this.formatSpec(modelSpecs.lengthIn, 25.4, unitMetric ? 0 : 1)) : ''}
                placeholder="—"
                onChange={(e) => this.updateSpec('lengthIn', e.target.value, unitMetric ? 1 / 25.4 : 1)}
              />
              <span className="v3-spec-unit">{unitMetric ? 'mm' : 'in'}</span>
            </div>
            {specErrors.length && (
              <div className="v3-specs-row-err">{specErrors.length}</div>
            )}
            <div className="v3-specs-row">
              <span className="v3-spec-lbl">Thickness</span>
              <input
                className={`v3-spec-val${specErrors.thickness ? ' v3-spec-val-error' : ''}`}
                type="number"
                value={modelSpecs ? specValue('thicknessIn', this.formatSpec(modelSpecs.thicknessIn, 25.4, unitMetric ? 1 : 2)) : ''}
                placeholder="—"
                onChange={(e) => this.updateSpec('thicknessIn', e.target.value, unitMetric ? 1 / 25.4 : 1)}
              />
              <span className="v3-spec-unit">{unitMetric ? 'mm' : 'in'}</span>
            </div>
            {specErrors.thickness && (
              <div className="v3-specs-row-err">{specErrors.thickness}</div>
            )}
            <div className="v3-specs-row">
              <span className="v3-spec-lbl">Weight</span>
              <input
                className="v3-spec-val"
                type="number"
                value={modelSpecs ? specValue('weightLbs', this.formatSpec(modelSpecs.weightLbs, 0.453592, 1)) : ''}
                placeholder="—"
                onChange={(e) => this.updateSpec('weightLbs', e.target.value, unitMetric ? 1 / 0.453592 : 1)}
              />
              <span className="v3-spec-unit">{unitMetric ? 'kg' : 'lbs'}</span>
            </div>
            <div className="v3-specs-row">
              <span className="v3-spec-lbl">Area</span>
              <input
                className="v3-spec-val"
                type="number"
                value={modelSpecs ? this.formatSpec(modelSpecs.areaFt, 0.092903, unitMetric ? 3 : 2) : ''}
                placeholder="—"
                readOnly
              />
              <span className="v3-spec-unit">{unitMetric ? 'm²' : 'ft²'}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderStep4() {
    const { selectedSystem, selectedRoof } = this.state;
    const isPitched = selectedSystem ? ['NXT', 'SM'].includes(selectedSystem.code) : false;
    const roofOptions = this.getRoofOptions();

    return (
      <div className="v3-modal-body">
        <p className="v3-modal-subtitle">
          {isPitched
            ? 'Select the surface material for this pitched roof installation.'
            : 'Select the membrane type for this flat roof installation.'}
        </p>
        <div className="v3-mini-card-wrap roof-cards">
          {roofOptions.map((roof) => {
            const key = String(roof.value);
            const isSelected = selectedRoof === key;
            return (
              <div
                key={key}
                className={`v3-mini-card${isSelected ? ' selected' : ''}`}
                onClick={() => this.setState({ selectedRoof: key })}
              >
                {isSelected && (
                  <div className="v3-mini-card-check">
                    <CheckIcon />
                  </div>
                )}
                <div className="v3-mini-card-icon">
                  {ROOF_ICONS[key]}
                </div>
                <div className="v3-mini-card-code" style={{ fontSize: 11, marginBottom: 0 }}>{roof.name}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  getStepTitle(): string {
    switch (this.state.step) {
      case 1: return 'Select a system type';
      case 2: return 'Project details';
      case 3: return 'Select module';
      case 4: return 'Select roof type';
      default: return '';
    }
  }

  render() {
    const { step } = this.state;
    const isFinal = step === 4;
    const skipFromStep2 = step === 2 && this.shouldSkipStepsFromProfile();

    return (
      <div className="v3-modal-overlay">
        <div className="v3-modal-card">
          {this.renderProgressBar()}

          <div className="v3-modal-top">
            <div>
              <div className="v3-modal-step-label">Step {step} of 4</div>
              <div className="v3-modal-title">{this.getStepTitle()}</div>
            </div>
            <button className="v3-modal-close" onClick={this.props.onClose}>
              <CloseIcon />
            </button>
          </div>

          {step === 1 && this.renderStep1()}
          {step === 2 && this.renderStep2()}
          {step === 3 && this.renderStep3()}
          {step === 4 && this.renderStep4()}

          <div className="v3-modal-footer">
            {step === 1 ? (
              <span style={{ fontSize: 12, color: '#5A7D95' }}>Select one to continue</span>
            ) : step > 1 ? (
              <button className="v3-btn v3-btn-ghost" onClick={this.goBack}>← Back</button>
            ) : <div />}
            <button
              className={`v3-btn v3-btn-primary${isFinal || skipFromStep2 ? ' v3-btn-lg' : ''}`}
              disabled={!this.canContinue() || this.props.creating}
              onClick={this.onContinue}
            >
              {(isFinal || skipFromStep2)
                ? (this.props.creating ? 'Creating project…' : 'Start Design →')
                : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(appState: any) {
  const lite = appState.liteProject || {};
  return {
    mfgs: appState.moduleSelector.mfgs || [],
    mfgsLoading: !!appState.moduleSelector.mfgsLoading || !!appState.moduleSelector.allInLoading,
    models: appState.moduleSelector.models || [],
    modelsLoading: !!appState.moduleSelector.modelsLoading,
    projectEnvConfig: appState.projectConfiguration ? appState.projectConfiguration.projectEnvConfig || {} : {},
    creating: !!lite.creating,
    userProfiles: Array.isArray(lite.userProfiles) ? lite.userProfiles : [],
    userProfilesLoading: !!lite.userProfilesLoading,
  };
}

export default connect(mapStateToProps)(NewProjectModal);
// Lite default row_spacing (inches) per product, mirroring legacy
// productsSettingsData.ts. row_spacing here is in inches, matching
// saveProject's projectEnvConfig.row_spacing.
function getRowSpacingLite(numericProductId: number): number {
  if (isSolarMount(numericProductId)) return 1;        // SM (9)
  if (isNxtHorizon(numericProductId)) return 0.5;      // NXT (25/26/27)
  switch (numericProductId) {
    case products.rm_10_evolution: return 13.5;         // RM10 EVO (28)
    case products.rm_gridflex_10d: return 13;           // GF 10D (33)
    case products.rm_gridflex_5d: return 7;             // GF 5D (36)
    default: return 1;
  }
}

// Lite default column_spacing (inches) per product, mirroring legacy
// productsSettingsData.ts.
function getColSpacingLite(numericProductId: number): number {
  if (isSolarMount(numericProductId)) return 1;        // SM (9)
  if (isNxtHorizon(numericProductId)) return 0.5;      // NXT (25/26/27)
  switch (numericProductId) {
    case products.rm_10_evolution: return 0.25;         // RM10 EVO (28)
    case products.rm_gridflex_10d: return 0.25;         // GF 10D (33)
    case products.rm_gridflex_5d: return 0.25;          // GF 5D (36)
    default: return 1;
  }
}
